import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Inbox, Mail, Trash2, ExternalLink, Upload, Loader2, Plus, DollarSign } from "lucide-react";

const adImg = (p) => `${API}/advertising/image/${p}`;
const inputCls = "w-full bg-ink border border-stroke px-3 py-2 font-mono text-sm text-paper placeholder:text-muted-gray focus:outline-none focus:border-acid";
const STATUSES = ["pending", "active", "expired", "rejected"];
const statusCls = {
  pending: "border-gold/50 bg-gold/10 text-gold",
  active: "border-acid/50 bg-acid/10 text-acid",
  expired: "border-stroke bg-elevated text-muted-gray",
  rejected: "border-magenta/50 bg-magenta/10 text-magenta",
};

export default function AdminAds() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ advertiser: "", brand: "", email: "", headline: "", link_url: "", tier: "" });
  const [imagePath, setImagePath] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const fetchItems = () =>
    api.get("/advertising/ads").then((r) => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));

  useEffect(() => {
    if (user?.role === "admin") { fetchItems(); api.get("/advertising/tiers").then((r) => setTiers(r.data)).catch(() => {}); }
    else setLoading(false);
  }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/app" replace />;

  const patch = async (id, body) => {
    try {
      const { data } = await api.patch(`/advertising/ads/${id}`, body);
      setItems((arr) => arr.map((x) => (x.id === id ? data : x)));
      toast.success("Updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/advertising/ads/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Ad deleted");
    } catch { toast.error("Delete failed"); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/advertising/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImagePath(data.path);
      toast.success("Banner uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const createAd = async (e) => {
    e.preventDefault();
    if (!form.advertiser.trim() || !form.link_url.trim() || !imagePath) return toast.error("Name, link and banner are required");
    setSaving(true);
    try {
      await api.post("/advertising/ads", { ...form, image_path: imagePath });
      toast.success("Ad created");
      setForm({ advertiser: "", brand: "", email: "", headline: "", link_url: "", tier: "" });
      setImagePath(""); setShowNew(false);
      if (fileRef.current) fileRef.current.value = "";
      fetchItems();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Create failed"); }
    finally { setSaving(false); }
  };

  const counts = STATUSES.reduce((a, s) => ({ ...a, [s]: items.filter((i) => (i.status || "pending") === s).length }), {});

  return (
    <div className="p-6 lg:p-10 max-w-6xl">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-acid">Admin</div>
          <h1 className="font-display text-5xl lg:text-6xl text-paper leading-none mt-1">AD MANAGER</h1>
          <div className="flex flex-wrap gap-2 mt-4">
            {STATUSES.map((s) => (
              <span key={s} className={`font-mono text-[10px] uppercase px-3 py-1.5 border ${statusCls[s]}`}>{s} · {counts[s]}</span>
            ))}
          </div>
        </div>
        <button onClick={() => setShowNew((v) => !v)} data-testid="ad-new-toggle"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider bg-acid text-ink font-bold px-5 py-3 hard-shadow">
          <Plus size={16} /> New Ad
        </button>
      </div>

      {showNew && (
        <form onSubmit={createAd} className="bg-surface border border-acid/40 p-6 mb-8 space-y-3" data-testid="ad-create-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <input className={inputCls} placeholder="Advertiser name" value={form.advertiser} onChange={(e) => setForm({ ...form, advertiser: e.target.value })} data-testid="ad-create-advertiser" />
            <input className={inputCls} placeholder="Brand / company" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
            <input className={inputCls} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <select className={inputCls} value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} data-testid="ad-create-tier">
              <option value="">Custom (no tier)</option>
              {tiers.map((t) => <option key={t.id} value={t.id}>{t.name} — ${t.price}/{t.months}mo</option>)}
            </select>
            <input className={inputCls} placeholder="Headline" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} />
            <input className={inputCls} placeholder="Click-through link (https://...)" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} data-testid="ad-create-link" />
          </div>
          {imagePath ? (
            <div className="flex items-center gap-3 border border-acid/40 p-2 bg-ink">
              <img src={adImg(imagePath)} alt="preview" className="h-14 w-24 object-cover border border-stroke" />
              <span className="font-mono text-xs text-acid flex-1">Banner uploaded</span>
              <button type="button" onClick={() => { setImagePath(""); if (fileRef.current) fileRef.current.value = ""; }} className="font-mono text-[10px] uppercase text-muted-gray hover:text-magenta">Replace</button>
            </div>
          ) : (
            <label className="flex items-center justify-center gap-2 border border-dashed border-stroke py-5 cursor-pointer hover:border-acid text-muted-gray hover:text-acid">
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              <span className="font-mono text-xs uppercase tracking-wider">{uploading ? "Uploading..." : "Upload banner"}</span>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="ad-create-image" />
            </label>
          )}
          <button type="submit" disabled={saving || uploading} data-testid="ad-create-submit"
            className="bg-acid text-ink font-bold font-mono text-xs uppercase tracking-wider px-6 py-3 hover:bg-glow disabled:opacity-50 flex items-center gap-2">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create Ad
          </button>
        </form>
      )}

      {loading ? (
        <div className="font-mono text-sm text-muted-gray animate-pulse">Loading...</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-stroke p-12 text-center">
          <Inbox className="text-muted-gray mx-auto mb-3" size={28} />
          <p className="font-mono text-sm text-muted-gray">No ads yet. Advertisers appear here, or add one manually.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="ad-list">
          {items.map((it) => (
            <div key={it.id} className="bg-surface border border-stroke p-4 flex flex-col lg:flex-row lg:items-center gap-4" data-testid="ad-row">
              {it.image_path && <img src={adImg(it.image_path)} alt="banner" className="h-16 w-28 object-cover border border-stroke shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display text-2xl text-paper">{it.headline || it.advertiser}</span>
                  {it.tier_name && <span className="font-mono text-[10px] uppercase px-2 py-0.5 border border-acid/40 text-acid">{it.tier_name}</span>}
                  <span className="font-mono text-[11px] text-gold flex items-center"><DollarSign size={11} />{it.amount} · {it.months}mo</span>
                </div>
                <div className="font-mono text-xs text-muted-gray mt-0.5">{it.advertiser}{it.brand ? ` · ${it.brand}` : ""}</div>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  {it.email && <a href={`mailto:${it.email}`} className="font-mono text-[11px] text-cyan hover:underline flex items-center gap-1"><Mail size={11} /> {it.email}</a>}
                  {it.link_url && <a href={it.link_url} target="_blank" rel="noopener noreferrer" className="font-mono text-[11px] text-cyan hover:underline flex items-center gap-1 truncate max-w-[220px]"><ExternalLink size={11} /> {it.link_url}</a>}
                </div>
                {it.end_at && <div className="font-mono text-[10px] text-muted-gray mt-1">Runs until {new Date(it.end_at).toLocaleDateString()}</div>}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => patch(it.id, { payment_status: it.payment_status === "paid" ? "unpaid" : "paid" })}
                  data-testid={`ad-pay-${it.id}`}
                  className={`font-mono text-[10px] uppercase px-3 py-2 border ${it.payment_status === "paid" ? "border-acid/50 bg-acid/10 text-acid" : "border-stroke text-muted-gray hover:text-paper"}`}>
                  {it.payment_status === "paid" ? "Paid ✓" : "Mark Paid"}
                </button>
                <select value={it.status || "pending"} onChange={(e) => patch(it.id, { status: e.target.value })}
                  data-testid={`ad-status-${it.id}`}
                  className={`bg-ink border px-3 py-2 font-mono text-xs uppercase focus:outline-none ${statusCls[it.status || "pending"]}`}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={() => remove(it.id)} data-testid={`ad-delete-${it.id}`} className="text-muted-gray hover:text-magenta p-2"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="font-mono text-[11px] text-muted-gray mt-6">
        Ads go live only when marked <span className="text-acid">Paid</span> and status <span className="text-acid">Active</span>. Marking an ad paid records the amount as advertising income in your Wallet.
      </p>
    </div>
  );
}
